/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import i18n from 'i18next';
import ChainedBackend from 'i18next-chained-backend';
import HttpBackend from 'i18next-http-backend';
// import StorageBackend from 'i18next-localstorage-backend';
import AsyncStorageBackend from 'i18next-async-storage-backend2';
import resourcesToBackend from 'i18next-resources-to-backend';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { initReactI18next } from 'react-i18next';
import merge from 'lodash/merge';
// import { Platform } from 'react-native';
import type { TranslationResources } from './LocaleProvider';
import {
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
  SupportedLocales,
} from './constants';

type NamespaceUrlMap = Record<string, string>;

export type TranslationJson = { [key: string]: string | TranslationJson };

export {
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
  SUPPORTED_LOCALES,
} from './constants';
export type { SupportedLocales } from './constants';

const LANGUAGE_STORAGE_KEY = 'user_preferred_language';

export const getLocalesVersions = (version: number) => {
  const v = `v1.0.${version}`;
  return buildLocalVersions(v, v, v, v, v, v, v, v, v, v, v, v, v, v);
};

export const buildLocalVersions = (
  enGb: string,
  idId: string,
  thTh: string,
  jaJp: string,
  koKr: string,
  msMy: string,
  viVn: string,
  zhCn: string,
  zhHk: string,
  zhTw: string,
  trTr: string,
  ruRu: string,
  kmKh: string,
  uzUz: string,
) => {
  return {
    'en-gb': enGb,
    'id-id': idId,
    'th-th': thTh,
    'ja-jp': jaJp,
    'ko-kr': koKr,
    'ms-my': msMy,
    'vi-vn': viVn,
    'zh-cn': zhCn,
    'zh-hk': zhHk,
    'zh-tw': zhTw,
    'tr-tr': trTr,
    'ru-ru': ruRu,
    'km-kh': kmKh,
    'uz-uz': uzUz,
  };
};

export const buildNamespace = (
  namespace: string,
  enGb: TranslationJson,
  idId: TranslationJson,
  thTh: TranslationJson,
  jaJp: TranslationJson,
  koKr: TranslationJson,
  msMy: TranslationJson,
  viVn: TranslationJson,
  zhCn: TranslationJson,
  zhHk: TranslationJson,
  zhTw: TranslationJson,
  trTr: TranslationJson,
  ruRu: TranslationJson,
  kmKh: TranslationJson,
  uzUz: TranslationJson,
) => {
  return {
    'en-gb': {
      [namespace]: enGb,
    },
    'id-id': {
      [namespace]: idId,
    },
    'th-th': {
      [namespace]: thTh,
    },
    'ja-jp': {
      [namespace]: jaJp,
    },
    'ko-kr': {
      [namespace]: koKr,
    },
    'ms-my': {
      [namespace]: msMy,
    },
    'vi-vn': {
      [namespace]: viVn,
    },
    'zh-cn': {
      [namespace]: zhCn,
    },
    'zh-hk': {
      [namespace]: zhHk,
    },
    'zh-tw': {
      [namespace]: zhTw,
    },
    'tr-tr': {
      [namespace]: trTr,
    },
    'ru-ru': {
      [namespace]: ruRu,
    },
    'km-kh': {
      [namespace]: kmKh,
    },
    'uz-uz': {
      [namespace]: uzUz,
    },
  };
};

export const buildEmptyNamespace = (namespace: string) => {
  return buildNamespace(
    namespace,
    {},
    {},
    {},
    {},
    {},
    {},
    {},
    {},
    {},
    {},
    {},
    {},
    {},
    {},
  );
};

export const buildResources = (
  ...namespaces: Record<string, Record<string, TranslationJson>>[]
) => {
  return namespaces.reduce((acc, ns) => merge(acc, ns), {});
};

export const buildBundledResources = (
  ...namespaces: Record<string, Record<string, TranslationJson>>[]
) => {
  const bundled: Record<string, Record<string, TranslationJson>> = {};

  for (const ns of namespaces) {
    for (const [lng, translationsByNs] of Object.entries(ns)) {
      if (!bundled[lng]) {
        bundled[lng] = { bundled: {} };
      }
      for (const [, translations] of Object.entries(translationsByNs)) {
        // Merge all namespace contents into one flat "bundled"
        bundled[lng].bundled = merge({}, bundled[lng].bundled, translations);
      }
    }
  }

  return bundled;
};

export async function removeOldTranslationKeys(translationsVersion: number) {
  console.log('removeOldTranslationKeys, latest Version:', translationsVersion);
  const prefix = 'i18next_res_';

  // if (Platform.OS === 'web') {
  //   for (let i = localStorage.length - 1; i >= 0; i--) {
  //     const key = localStorage.key(i);
  //     if (key && key.startsWith(prefix)) {
  //       const match = key.match(/^i18next_res_(\d+)_/);
  //       if (match && Number(match[1]) !== translationsVersion) {
  //         console.log('removing old Translation key:', key);
  //         localStorage.removeItem(key);
  //       }
  //     }
  //   }
  // } else {
  try {
    const keys = await AsyncStorage.getAllKeys();
    const keysToRemove = keys.filter((key) => {
      if (!key.startsWith(prefix)) return false;
      const match = key.match(/^i18next_res_(\d+)_/);
      return match ? Number(match[1]) !== translationsVersion : false;
    });
    if (keysToRemove.length > 0) {
      console.log('removing old Translation keys:', keysToRemove);
      await AsyncStorage.multiRemove(keysToRemove);
    }
  } catch (e) {
    console.error('Error removing keys:', e);
  }
  // }
}

/**
 * Get saved language from AsyncStorage
 * @returns Saved language or null if not found
 */
export const getSavedLanguage = async (): Promise<SupportedLocales | null> => {
  try {
    const savedLanguage = await AsyncStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (savedLanguage && isLanguageSupported(savedLanguage)) {
      return savedLanguage as SupportedLocales;
    }
    return null;
  } catch {
    return null;
  }
};

/**
 * Save language preference to AsyncStorage
 * @param language The language to save
 */
export const saveLanguage = async (
  language: SupportedLocales,
): Promise<boolean> => {
  try {
    await AsyncStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    return true;
  } catch {
    return false;
  }
};

/**
 * Initialize i18n with dynamic configuration
 * @param env stg or prd
 * @param onlyBundledResources if true, only use bundled resources, if false, use remote resources, used for local development
 * @param translationsVersion to force reload locales
 * @param bundledResources merged bundled locals
 * @param nsUrlMap to allow lob to provide custom locales urls
 */
export function initI18n(
  env: 'stg' | 'prd',
  onlyBundledResources: boolean = true,
  translationsVersion: number = 1,
  bundledResources?: TranslationResources,
  nsUrlMap: NamespaceUrlMap = {},
) {
  console.log('initI18n, latest Version: ', translationsVersion);
  if (!i18n.isInitialized && !i18n.isInitializing) {
    const BundledBackend = resourcesToBackend(
      bundledResources ?? buildEmptyNamespace('bundled'),
    );
    let backends = [
      // 1 serve locales from cache
      AsyncStorageBackend,
      // Platform.OS == 'web' ? StorageBackend : AsyncStorageBackend,
      // 2 serve locales from remote
      HttpBackend,
      // 3 serve locales from bundled
      BundledBackend,
    ];
    let backendOptions = [
      {
        expirationTime: 7 * 24 * 60 * 60 * 1000, // 7 days
        // prefix: `i18next_res_`,
        versions: getLocalesVersions(translationsVersion),
        skipCache: ['bundled'],
      },
      {
        loadPath: (lng: string, ns: string) => {
          if (ns === 'bundled' || ns[0] === 'bundled') return null; // skip fetching bundled

          // load lobs locales
          const url = nsUrlMap[ns];
          if (url) {
            return url
              .replace('{{lng}}', lng)
              .replace('{{version}}', String(translationsVersion));
          }

          // default locales path
          return `https://static.${
            env === 'stg' ? 'stgairasia' : 'airasia'
          }.com/mobile/config/locales/${ns}/${lng}.json?v=${translationsVersion}`;
        },
      },
    ];
    if (onlyBundledResources) {
      backends = [BundledBackend];
      backendOptions = [];
    }
    i18n
      .use(initReactI18next)
      .use(ChainedBackend)
      .init({
        supportedLngs: Object.keys(SUPPORTED_LANGUAGES),
        // ns will make sure to prefetch remote locales, if not set here, it will lazily load, bundled needs to be the last one
        ns: ['account', 'bundled'],
        // this will help if remote has missing keys
        fallbackNS: 'bundled',
        defaultNS: 'bundled',
        fallbackLng: DEFAULT_LANGUAGE,
        debug: process.env.NODE_ENV === 'development',
        interpolation: {
          escapeValue: false, // React already escapes values
        },
        lowerCaseLng: true,
        backend: {
          backends,
          backendOptions,
        },
        load: 'currentOnly',
      })
      .catch((err) => {
        console.error('i18n init failed:', err);
      });
  } else if (__DEV__ && i18n.isInitialized) {
    // for local development
    i18n.reloadResources();
  }
}

// Function to change language
export const changeLanguage = async (language: SupportedLocales) => {
  try {
    await i18n.changeLanguage(language);
    await saveLanguage(language);
    return true;
  } catch (error) {
    console.error('Failed to change language:', error);
    return false;
  }
};

// Function to check if language is supported
export const isLanguageSupported = (
  language: string,
): language is SupportedLocales => {
  return language in SUPPORTED_LANGUAGES;
};

export default i18n;
